// Angualr imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

// 3rd party imports
import { Subscription } from 'rxjs';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Constant imports
import { Constants } from '../../../../_constants/constants';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';

// Service imports
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { GLBatchService } from '../../../shared-cirabooks-module/_services/general-ledger/gl-batch.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { DetailPopupsService } from '../../_services/detail-popups.service';

// Model imports
import { GLBatchDetailParametersDto } from '../../_models/general-ledger/gl-batch/gl-batch-detail-parameters-dto';
import { GLBatchDetailResultDto } from '../../_models/general-ledger/gl-batch/gl-batch-detail-result-dto';
import { KeyValueResultDto } from '../../../../_models/key-value-result-dto';
import { GLBatchDetailItemResultDto } from '../../_models/general-ledger/gl-batch/gl-batch-detail-item-result-dto';

@Component({
    selector: 'app-gl-batch-detail',
    templateUrl: './gl-batch-detail.component.html',
    styleUrls: ['./gl-batch-detail.component.scss']
})
export class GLBatchDetailComponent implements OnInit, OnDestroy {
    @Input() visible: boolean;
    @Input() glBatchDetailParam: GLBatchDetailParametersDto;
    @Output() closeGLBatchDetail = new EventEmitter<boolean>();

    loadingVisible = false;
    defaultLabelLocation = Constants.defaultFormLabelLocation;
    dateFormat = StringFormatConstants.dateFormat;
    currencyFormat: any;

    glBatchDetailData: GLBatchDetailResultDto = new GLBatchDetailResultDto();
    glBatchDetailItems: GLBatchDetailItemResultDto[] = [];
    selectedGLAccount = 'all';
    glAccounts: KeyValueResultDto[] = [];

    iconRefresh = IconConstants.REFRESH;
    glAccountColumns: any[] = [];
    glAccountGridBoxValue: any[] = [];

    forceReloadSubscription: Subscription;

    constructor(
        private readonly glBatchService: GLBatchService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly helperService: HelperService,
        private readonly detailPopupsService: DetailPopupsService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
    }

    ngOnInit() {
        if (!!this.glBatchDetailParam) {
            this.populateGLBatchDetail();
        }
        this.getGLAccountColumns();

        this.forceReloadSubscription = this.detailPopupsService.onForceReload().subscribe(() => {
            this.populateGLBatchDetail();
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.forceReloadSubscription)) {
            this.forceReloadSubscription.unsubscribe();
        }
    }

    calculateCustomSummary(e): void {
        this.helperService.calculateTotalSummary(e);
    }

    closePopup(): void {
        this.visible = false;
        this.closeGLBatchDetail.emit(false);
    }

    onRefreshClick(): void {
        if (this.selectedGLAccount === 'all') {
            this.glBatchDetailItems = this.glBatchDetailData.items;
        } else {
            this.glBatchDetailItems = this.glBatchDetailData.items.filter(
                (r) => r.glAccountNumber === this.selectedGLAccount
            );
        }
    }

    hasHyperlink(rec: GLBatchDetailItemResultDto): boolean {
        return (
            Utils.notNullOrEmpty(rec.sourceType) &&
            Utils.notNullOrEmpty(rec.sourceTypeCode) &&
            this.detailPopupsService.hasDetailsPopup(
                rec.sourceType,
                rec.sourceTypeCode,
                this.glBatchDetailParam.batchId
            )
        );
    }

    onAmountClick(rec: GLBatchDetailItemResultDto): void {
        if (Utils.notNullAndDefined(rec)) {
            this.detailPopupsService.showDetailsPopup(rec.sourceType, rec.sourceTypeCode, {
                managementCompanyId: this.glBatchDetailParam.managementCompanyId,
                communityId: this.glBatchDetailParam.communityId,
                headerId: rec.sourceHeaderId,
                vendorCode: rec.vendorCustomerCode
            });
        }
    }

    onGLAccountSelection(selectedObject: any): void {
        if (
            Utils.notNullAndDefined(selectedObject) &&
            Utils.notNullOrEmpty(selectedObject.selectedRow) &&
            Utils.notEqualsIgnoreCase(this.selectedGLAccount, selectedObject.selectedRow[0].key)
        ) {
            const glAccount = selectedObject.selectedRow[0];
            this.glAccountGridBoxValue = [glAccount.key];
            this.selectedGLAccount = glAccount.key;
            this.onRefreshClick();
        } else {
            this.selectedGLAccount = null;
        }
    }

    private populateGLBatchDetail(): void {
        this.loadingVisible = true;
        this.glBatchService.getGLBatchDetail(this.glBatchDetailParam).subscribe(
            (result) => {
                this.glAccounts = result.items
                    .map((r) => {
                        const rec = new KeyValueResultDto();
                        rec.key = r.glAccountNumber;
                        rec.displayText = r.glAccountDisplayText || r.glAccountNumber;
                        return rec;
                    })
                    .filter((k, i, l) => l.findIndex((x) => x.key === k.key) === i);
                const all: KeyValueResultDto = new KeyValueResultDto();
                all.key = 'all';
                all.displayText = 'ALL';
                all.isDefault = false;
                this.glAccounts.push(all);
                this.glBatchDetailData = result;
                this.glBatchDetailItems = result.items;
                this.glAccountGridBoxValue = [all.key];
                this.onRefreshClick();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
                this.closePopup();
            }
        );
    }

    private getGLAccountColumns(): void {
        this.glAccountColumns = [
            {
                dataField: 'displayText',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'key',
                dataType: 'string',
                caption: 'GL Account No'
            }
        ];
    }
}
